import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Coins } from "lucide-react";
import { adminListCreditPacks, adminCreateCreditPack, adminUpdateCreditPack, adminDeleteCreditPack } from "../../../lib/api";

export function CreditPacksAdminPanel() {
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({
        title: "",
        description: "",
        credits: "",
        price_usd: "",
        revenuecat_product_id: "",
        sort_order: 0,
    });

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            setPacks(await adminListCreditPacks());
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load packs");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const create = async (e) => {
        e?.preventDefault?.();
        const credits = parseInt(form.credits, 10);
        if (!form.title.trim() || !credits || credits <= 0) {
            toast.error("Title + a positive credit amount are required.");
            return;
        }
        try {
            await adminCreateCreditPack({
                title: form.title.trim(),
                description: form.description.trim(),
                credits,
                price_usd: parseFloat(form.price_usd || 0) || 0,
                revenuecat_product_id: form.revenuecat_product_id.trim(),
                sort_order: parseInt(form.sort_order || 0, 10) || 0,
                active: true,
            });
            toast.success("Pack created");
            setForm({
                title: "",
                description: "",
                credits: "",
                price_usd: "",
                revenuecat_product_id: "",
                sort_order: 0,
            });
            refresh();
        } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Couldn't create pack");
        }
    };

    const toggleActive = async (p) => {
        try {
            await adminUpdateCreditPack(p.id, { active: !p.active });
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.title}"?`)) return;
        try {
            await adminDeleteCreditPack(p.id);
            toast.success("Pack deleted");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <section
            className="mt-10 rounded-md border border-[#2A3655] bg-[#0F1B3A] p-5"
            data-testid="admin-credit-packs"
        >
            <div className="flex items-center gap-2 mb-4">
                <Coins className="w-5 h-5 text-amber-300" />
                <h3 className="text-lg sm:text-xl font-black tracking-tight text-white">
                    Credit packs (one-time top-ups)
                </h3>
            </div>
            <p className="text-[12px] text-slate-400 mb-4 max-w-prose">
                Packs are bonus credits users buy outside their subscription —
                they never expire and add to whatever's left in the monthly bucket.
                Wire each pack to a RevenueCat product ID to make it purchasable.
            </p>

            <form
                onSubmit={create}
                className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-5"
                data-testid="admin-pack-form"
            >
                <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Title (e.g. Starter 100)"
                    className="md:col-span-2 px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-white text-sm"
                />
                <input
                    type="number"
                    min="1"
                    value={form.credits}
                    onChange={(e) => setForm({ ...form, credits: e.target.value })}
                    placeholder="Credits"
                    className="px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-white text-sm"
                />
                <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.price_usd}
                    onChange={(e) => setForm({ ...form, price_usd: e.target.value })}
                    placeholder="Price (USD)"
                    className="px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-white text-sm"
                />
                <input
                    type="text"
                    value={form.revenuecat_product_id}
                    onChange={(e) => setForm({ ...form, revenuecat_product_id: e.target.value })}
                    placeholder="RC product ID"
                    className="px-3 py-2 bg-[#0A1128] border border-[#2A3655] rounded-sm text-white text-sm font-mono"
                />
                <button
                    type="submit"
                    data-testid="admin-pack-create"
                    className="px-3 py-2 text-sm font-bold rounded-sm bg-amber-500 hover:bg-amber-400 text-[#0A1128]"
                >
                    Add pack
                </button>
            </form>

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                        <tr className="border-b border-[#2A3655]">
                            <th className="text-left py-2">Title</th>
                            <th className="text-left py-2">Credits</th>
                            <th className="text-left py-2">Price</th>
                            <th className="text-left py-2">RC product</th>
                            <th className="text-left py-2">Status</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={6} className="py-4 text-center text-slate-500">
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && packs.length === 0 && (
                            <tr>
                                <td colSpan={6} className="py-4 text-center text-slate-500">
                                    No packs yet — add one above.
                                </td>
                            </tr>
                        )}
                        {packs.map((p) => (
                            <tr key={p.id} className="border-b border-[#2A3655]/60 text-slate-200">
                                <td className="py-2 font-semibold">{p.title}</td>
                                <td className="py-2">{p.credits}</td>
                                <td className="py-2">
                                    {p.price_usd > 0 ? `$${p.price_usd.toFixed(2)}` : "—"}
                                </td>
                                <td className="py-2 font-mono text-[11px] text-slate-400">
                                    {p.revenuecat_product_id || "—"}
                                </td>
                                <td className="py-2">
                                    <button
                                        type="button"
                                        onClick={() => toggleActive(p)}
                                        data-testid={`admin-pack-toggle-${p.id}`}
                                        className={`text-[11px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${
                                            p.active
                                                ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10"
                                                : "border-slate-500/60 text-slate-400 bg-slate-500/10"
                                        }`}
                                    >
                                        {p.active ? "Active" : "Inactive"}
                                    </button>
                                </td>
                                <td className="py-2 text-right">
                                    <button
                                        type="button"
                                        onClick={() => remove(p)}
                                        data-testid={`admin-pack-delete-${p.id}`}
                                        className="text-slate-400 hover:text-rose-400"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}


// ----- iter-95: Promo codes admin -----

